import {
  memo, useEffect, useState,
} from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';

import styles from './HelixConfigModal.module.scss';

export interface HelixConfigData {
  base_url?: string;
  tenant?: string;
  token_masked?: string;
  has_token?: boolean;
  is_active?: boolean;
  last_tested_at?: string;
  last_status?: 'connected' | 'error' | 'not_configured';
  last_latency_ms?: number;
  last_error?: string;
}

type OwnProps = {
  isOpen: boolean;
  initialConfig?: HelixConfigData;
  isSaving?: boolean;
  onClose: NoneToVoidFunction;
  onSave: (payload: { base_url: string; tenant: string; token?: string }) => void;
};

import { CRM_BACKEND_URL as BACKEND_URL } from '../../util/crmBackendUrl';

const HelixConfigModal = ({
  isOpen,
  initialConfig,
  isSaving,
  onClose,
  onSave,
}: OwnProps) => {
  const [baseUrl, setBaseUrl] = useState('');
  const [tenant, setTenant] = useState('medicina');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | undefined>(undefined);

  useEffect(() => {
    if (initialConfig) {
      setBaseUrl(initialConfig.base_url || 'https://helix.med.umss.edu.bo/api/v1');
      setTenant(initialConfig.tenant || 'medicina');
      setToken('');
      setTestResult(undefined);
    }
  }, [initialConfig, isOpen]);

  const handleToggleShowToken = useLastCallback(() => {
    setShowToken((prev) => !prev);
  });

  const handlePasteToken = useLastCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setToken(text.trim());
      }
    } catch {
      // Portapapeles no disponible o denegado
    }
  });

  const handleTestConnection = useLastCallback(async () => {
    setIsTesting(true);
    setTestResult(undefined);
    try {
      const payload: { base_url: string; tenant: string; token?: string } = {
        base_url: baseUrl.trim(),
        tenant: tenant.trim().toLowerCase(),
      };
      if (token.trim()) {
        payload.token = token.trim();
      }

      const res = await fetch(`${BACKEND_URL}/helix/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setTestResult({
          success: true,
          message: data.message || `Conexión verificada exitosamente (${data.latency_ms} ms).`,
        });
      } else {
        setTestResult({
          success: false,
          message: data.message || data.detail || 'Error al conectar con HELIX.',
        });
      }
    } catch (err: any) {
      setTestResult({
        success: false,
        message: `Error de red: ${err.message || 'No se pudo contactar al servidor.'}`,
      });
    } finally {
      setIsTesting(false);
    }
  });

  const handleSave = useLastCallback(() => {
    if (!baseUrl.trim()) {
      alert('Por favor ingresa la URL Base de la API HELIX.');
      return;
    }
    if (!tenant.trim()) {
      alert('Por favor ingresa el slug del Tenant (ej: medicina).');
      return;
    }

    onSave({
      base_url: baseUrl.trim(),
      tenant: tenant.trim().toLowerCase(),
      token: token.trim() || undefined,
    });
  });

  if (!isOpen) return undefined;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.headerIcon}>
              <Icon name="link" />
            </div>
            <div>
              <h3 className={styles.headerTitle}>Interconexión HELIX (M2M)</h3>
              <p className={styles.headerSubtitle}>
                Gestión académica, expediente de postulantes y validación de matrículas
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <Icon name="close" />
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          <div className={styles.infoNotice}>
            <div className={styles.infoIcon}>
              <Icon name="admin" />
            </div>
            <p className={styles.infoText}>
              <strong>Parámetros de Red Universitaria (UMSS):</strong>
              {' '}
              El servidor de HELIX valida las peticiones mediante la cabecera
              {' '}
              <code>X-Helix-Token</code>
              {' '}
              y la lista blanca de IP autorizada (
              <code>167.157.38.70</code>
              ).
            </p>
          </div>

          {/* URL Base */}
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabelRow}>
              <span className={styles.label}>URL Base de la API HELIX</span>
              <span className={styles.helperText}>Intranet UMSS o dominio HTTPS</span>
            </div>
            <input
              type="text"
              className={styles.input}
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://helix.med.umss.edu.bo/api/v1"
            />
          </div>

          {/* Tenant */}
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabelRow}>
              <span className={styles.label}>Slug del Tenant (Unidad Académica)</span>
              <span className={styles.helperText}>Sub-ruta multitenant en HELIX</span>
            </div>
            <input
              type="text"
              className={styles.input}
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              placeholder="medicina"
            />
          </div>

          {/* Token M2M */}
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabelRow}>
              <span className={styles.label}>Token de Seguridad M2M (X-Helix-Token)</span>
              <span className={styles.helperText}>
                {initialConfig?.has_token
                  ? `Configurado: ${initialConfig.token_masked} (Dejar vacío para no cambiar)`
                  : 'Token criptográfico de 64 caracteres hex'}
              </span>
            </div>
            <div className={styles.tokenInputWrapper}>
              <input
                type={showToken ? 'text' : 'password'}
                className={styles.tokenInput}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder={initialConfig?.has_token
                  ? '••••••••••••••••••••••••••••••••'
                  : 'Pega el token aquí...'}
              />
              <div className={styles.tokenActions}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={handlePasteToken}
                  title="Pegar del portapapeles"
                  aria-label="Pegar del portapapeles"
                >
                  <Icon name="copy" />
                </button>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={handleToggleShowToken}
                  title={showToken ? 'Ocultar token' : 'Mostrar token'}
                  aria-label={showToken ? 'Ocultar token' : 'Mostrar token'}
                >
                  <Icon name={showToken ? 'eye-crossed' : 'eye'} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {testResult && (
            <div className={buildClassName(
              styles.testFeedback,
              !testResult.success && styles.testFeedbackError,
            )}
            >
              {testResult.message}
            </div>
          )}

          <div className={styles.footerRow}>
            <button
              type="button"
              className={styles.btnTest}
              onClick={handleTestConnection}
              disabled={isTesting}
            >
              <Icon name="play" />
              <span>{isTesting ? 'Probando...' : 'Probar Conexión'}</span>
            </button>

            <div className={styles.footerButtons}>
              <button
                type="button"
                className={styles.btnCancel}
                onClick={onClose}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={styles.btnSave}
                onClick={handleSave}
                disabled={isSaving}
              >
                <Icon name="check" />
                <span>{isSaving ? 'Guardando...' : 'Guardar'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(HelixConfigModal);
